import type { SnapshotPosition } from "../../lib/api";

/**
 * Wallet-level blended APY. Denominator is the **total** wallet value
 * (positions + cash), not just positions, so the number reflects the
 * actual yield on every dollar in the wallet. Cash contributes 0%, so
 * holding cash drags blended APY down — that's the point: it tells the
 * operator how much capital is actually earning.
 */
export function computeBlendedApy(
	positions: SnapshotPosition[],
	totalValueUsdcRaw: string,
): number {
	const total = BigInt(totalValueUsdcRaw);
	if (total === 0n) return 0;
	const totalNum = Number(total);
	let weightedApy = 0;
	for (const p of positions) {
		weightedApy +=
			Number(p.expectedNetApy) * (Number(BigInt(p.valueUsdcRaw)) / totalNum);
	}
	return weightedApy;
}

/** Whole-USDC amount string → raw 6-decimal bigint, or null if unparsable. */
export function parseUsdcAmount(input: string): bigint | null {
	const trimmed = input.trim();
	if (trimmed === "") return null;
	if (!/^\d+(\.\d{0,6})?$/.test(trimmed)) return null;
	const [whole, frac = ""] = trimmed.split(".");
	const fracPadded = `${frac}000000`.slice(0, 6);
	try {
		return BigInt(whole) * 1_000_000n + BigInt(fracPadded);
	} catch {
		return null;
	}
}

/**
 * Raw 6-decimal USDC bigint → display string that parses **losslessly**
 * back through `parseUsdcAmount`.
 *
 * Used by the withdraw composer's "max" button: clicking max needs to
 * populate the input with a value that equals the position's exact
 * balance — not one cent over.
 *
 * Round-trip via `(Number(raw) / 1_000_000).toFixed(2)` is broken
 * for two reasons: (1) `toFixed(2)` rounds the 3rd decimal up, so a
 * balance of $149,867.745678 ends up as "149867.75" which parses
 * back to 149_867_750_000 — exceeding the actual balance by 4322
 * micro-USDC and tripping the "exceeds available balance" validator;
 * (2) `Number` loses precision above 2^53. This helper sidesteps both
 * by formatting the bigint directly, full precision, no rounding.
 *
 * Trailing zeros are stripped for readability, but at least two
 * decimals are kept so trivial amounts (e.g. 1 USDC) render as
 * "1.00" rather than "1." (which the input would have to special-case).
 */
export function rawUsdcToInputString(raw: bigint): string {
	const whole = raw / 1_000_000n;
	const frac = (raw % 1_000_000n).toString().padStart(6, "0");
	const trimmed = frac.replace(/0+$/, "");
	const fracOut = trimmed.length < 2 ? frac.slice(0, 2) : trimmed;
	return `${whole}.${fracOut}`;
}
